import {
  Color,
  ColorMaterialProperty,
  PolylineArrowMaterialProperty,
  PolylineDashMaterialProperty,
  PolylineGlowMaterialProperty,
  PolylineOutlineMaterialProperty,
} from "@cesium/engine";
import iro from "@jaames/iro";
import { DitapAlert, DitapMobileSelectPopup, Utils } from "../../..";
import LinestringEntity from "../edit/LinestringEntity";
import DitapPopup from "./DitapPopup";

/**
 *
 * LinestringEntity 스타일 변경 팝업 클래스.</br>
 * A pop-up class for changing the style of a LinestringEntity.
 * @extends DitapPopup
 *
 * @alias PopupStyleLinestring
 * @constructor
 *
 * @param {LinestringEntity} LinestringEntity 사용할 LinestringEntity 인스턴스.</br>The instance of LinestringEntity to change style.
 *
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.97759025002786,
 *   37.56679755138474,
 *    400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *  destination: destination,
 * });
 *
 * const position1 = new Ditap.Cartesian3.fromDegrees(
 *   126.97759025002786,
 *   37.56679755138474
 * );
 * const position2 = new Ditap.Cartesian3.fromDegrees(
 *   126.97764810657144,
 *   37.566518888624664
 * );
 *
 * // LinestringEntity instance 생성
 * const linestringEntity = new Ditap.LinestringEntity(viewer, [
 *   position1,
 *   position2,
 * ]);
 *
 * const popup = new Ditap.PopupStyleLinestring(linestringEntity);
 */
class PopupStyleLinestring extends DitapPopup {
  constructor(linestringEntity, ditapPaneManager) {
    // ...
    // 부모
    const parent = linestringEntity?.viewer?.container;

    super(parent, ditapPaneManager);

    this.linestringEntity = linestringEntity;
    // ENTITY
    if (linestringEntity instanceof LinestringEntity) {
      this.linestringEntity = linestringEntity;
    } else {
      console.error("Type doesn't match");
    }

    // 모바일일 경우 css 추가
    this.wrapper.classList.add("ditap-popup-mobile");
    this.wrapper.classList.add("ditap-style-change-popup");

    // 스타일 박스 생성
    const styleBox = this.createStyleBox();

    // 헤더 추가
    this.prependHeader(styleBox["header"]["title"]);

    // 컨텐츠 추가
    this.appendSubTitle(styleBox["content"]["subTitle"]);
    this.appendContent(styleBox["content"]["styleList"]);

    // 푸터 추가
    this.appendFooter(styleBox["footer"]["btnWrap"]);

    // 노드 세팅
    this.cancleBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-cancle-btn"
    )[0];
    this.okBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-ok-btn"
    )[0];
    this.typeSelectDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-select"
    )[0];
    this.typeSelectBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-select-btn"
    )[0];
    this.typeSelectInput = this.wrapper.getElementsByClassName(
      "js-ditap-style-type"
    )[0];
    this.selectLineType = this.wrapper.getElementsByClassName(
      "js-ditap-line-type"
    )[0];
    this.typeOptionDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-type-opt"
    )[0];
    this.widthInput = this.wrapper.getElementsByClassName(
      "js-ditap-style-width"
    )[0];
    this.addWidthBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-add-width"
    )[0];
    this.subtractWidthBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-subtract-width"
    )[0];
    this.widthBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-width-box"
    )[0];
    this.vertexBtn = this.wrapper.getElementsByClassName(
      "js-ditap-vertex-item"
    )[0];
    this.vertexCheckBox = this.wrapper.getElementsByClassName(
      "js-ditap-vertex-check-box"
    )[0];
    this.colorBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-color-box"
    )[0];
    this.colorBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-color-btn"
    )[0];
    this.colorInput = this.wrapper.getElementsByClassName(
      "js-ditap-style-color"
    )[0];
    this.colorPickBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-color-pickBox"
    )[0];
    this.colorPickerDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-color-picker"
    )[0];
    this.colorPickerOpenBtn = this.wrapper.getElementsByClassName(
      "js-ditap-color-open-btn"
    )[0];
    this.colorLists = this.wrapper
      .getElementsByClassName("js-ditap-style-color-list")[0]
      .getElementsByTagName("button");

    this.contourColorBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-contour-color-box"
    )[0];
    this.contourColorBtn = this.contourColorBox.getElementsByClassName(
      "js-ditap-style-color-btn"
    )[0];
    this.contourColorInput = this.contourColorBox.getElementsByClassName(
      "js-ditap-style-color"
    )[0];
    this.contourColorPickBox = this.contourColorBox.getElementsByClassName(
      "js-ditap-style-color-pickBox"
    )[0];
    this.contourColorPickerDiv = this.contourColorBox.getElementsByClassName(
      "js-ditap-style-color-picker"
    )[0];
    this.contourColorPickerOpenBtn = this.contourColorBox.getElementsByClassName(
      "js-ditap-color-open-btn"
    )[0];
    this.contourColorLists = this.contourColorBox
      .getElementsByClassName("js-ditap-style-color-list")[0]
      .getElementsByTagName("button");

    // 스타일 색상 선택 박스 생성
    this.colorPicker = this.createColorPicker(this.colorPickerDiv);

    // 스타일 윤곽 색상 선택 박스 생성
    this.contourColorPicker = this.createColorPicker(
      this.contourColorPickerDiv
    );

    // contentDiv
    this.contentDiv = this.wrapper.getElementsByClassName(
      "ditap-popup-style-list"
    )[0];

    // 팝업 오픈
    this.open();

    // 경고 팝업 추가
    this.createAlert();

    this.createMobilePopup(parent);

    // 팝업 버튼 이벤트 추가
    this.createStyleBoxEvent();

    // 기존 스타일 적용
    this.setPopupStyleLinestring(this.linestringEntity);

    this.changeCloseBtn();
    // 드래그 기능 적용
    this.isMobile = Utils.isBodyWidthLessThan600();
    if (!this.isMobile) {
      this.enableDrag();
      this.enableTouch();
    }

    if (this.btnWrap) {
      const fullBtn = document.createElement("button");
      fullBtn.classList.add("ditap-popup-full-btn", "ditap-custom-button");

      this.btnWrap.prepend(fullBtn);
      this.fullBtn = this.btnWrap;

      this.fullBtn.addEventListener(
        "click",
        (this.toggleExpandPopup = () => {
          if (this.wrapper.classList.contains("expanded")) {
            this.wrapper.classList.remove("expanded");
          } else {
            this.wrapper.classList.add("expanded");
          }
        })
      );
    }

    this.createResizeEvent();
  }

  createMobilePopup(parent) {
    // 1. 스타일 팝업
    this.mobileTypeSelectPopup = new DitapMobileSelectPopup(parent);
    this.mobileTypeSelectPopup.addTitle("스타일");
    this.mobileTypeSelectPopup.closeCallback = () => {
      this.typeOptionDiv.classList.add("hide");
      this.typeSelectDiv.classList.remove("on");
    };

    // 2. 색상 팝업
    this.mobileColorPickerPopup = new DitapMobileSelectPopup(parent);
    this.mobileColorPickerPopup.addTitle("색상");

    // 버튼 추가
    const btnWrap = document.createElement("div");
    const okBtn = document.createElement("button");
    const cancleBtn = document.createElement("button");
    okBtn.textContent = "확인";
    cancleBtn.textContent = "취소";
    okBtn.classList.add("ditap-popup-btn", "gold");
    btnWrap.classList.add("ditap-btn-wrap");
    okBtn.classList.add("ditap-popup-btn", "gold");
    cancleBtn.classList.add("ditap-popup-btn");
    btnWrap.appendChild(cancleBtn);
    btnWrap.appendChild(okBtn);
    this.mobileColorPickerPopup.appendFooter(btnWrap);

    okBtn.addEventListener("click", () => {
      const color = this.mobileColorInput.value;
      if (color.length === 7 && this.checkAvailableColor(color)) {
        this.mobileColorPickerPopup.close();
      } else {
        this.colorAlert.open();
        this.mobileColorInput.value = "#FFFFFF";
        this.colorPicker.color.red = 255;
        this.colorPicker.color.green = 255;
        this.colorPicker.color.blue = 255;
      }
    });

    cancleBtn.addEventListener("click", () => {
      this.mobileColorPickerPopup.close();
      this.changeColorPickerByInput(
        this.originInputValue,
        this.colorPicker,
        this.colorAlert,
        this.colorInput
      );
      this.colorPickBox.classList.add("hide");
    });

    const div = document.createElement("div");
    const inp = document.createElement("input");
    inp.classList.add("ditap-style-inp");
    inp.type = "text";
    inp.maxLength = "7";
    inp.value = this.contourColorInput.value;
    div.append(inp);
    this.mobileColorPickerPopup.appendContent(div);

    this.mobileColorInput = inp;

    // 3. 윤곽 색상 박스 생성
    this.mobileContourColorPickerPopup = new DitapMobileSelectPopup(parent);
    this.mobileContourColorPickerPopup.addTitle("색상");

    // 버튼 추가
    const btnWrap1 = document.createElement("div");
    const okBtn1 = document.createElement("button");
    const cancleBtn1 = document.createElement("button");
    okBtn1.textContent = "확인";
    cancleBtn1.textContent = "취소";
    okBtn1.classList.add("ditap-popup-btn", "gold");
    btnWrap1.classList.add("ditap-btn-wrap");
    okBtn1.classList.add("ditap-popup-btn", "gold");
    cancleBtn1.classList.add("ditap-popup-btn");
    btnWrap1.appendChild(cancleBtn1);
    btnWrap1.appendChild(okBtn1);
    this.mobileContourColorPickerPopup.appendFooter(btnWrap1);

    okBtn1.addEventListener("click", () => {
      const color = this.mobileContourColorInput.value;
      if (color.length === 7 && this.checkAvailableColor(color)) {
        this.mobileContourColorPickerPopup.close();
      } else {
        this.colorAlert.open();
        this.mobileContourColorInput.value = "#FFFFFF";
        this.contourColorPicker.color.red = 255;
        this.contourColorPicker.color.green = 255;
        this.contourColorPicker.color.blue = 255;
      }
    });

    cancleBtn1.addEventListener("click", () => {
      this.mobileContourColorPickerPopup.close();
      this.changeColorPickerByInput(
        this.originContourInputValue,
        this.contourColorPicker,
        this.contourColorAlert,
        this.contourColorInput
      );
      this.colorPickBox.classList.add("hide");
    });

    // 색상 박스 생성
    const div1 = document.createElement("div");
    const inp1 = document.createElement("input");
    inp1.classList.add("ditap-style-inp");
    inp1.type = "text";
    inp1.maxLength = "7";
    inp1.value = this.contourColorInput.value;
    div1.append(inp1);
    this.mobileContourColorPickerPopup.appendContent(div1);

    this.mobileContourColorInput = inp1;
  }

  createAlert() {
    // 경고창 생성
    this.colorAlert = new DitapAlert({
      type: "warning",
      title: "경고",
      content1: "올바른 색상값을 입력해주세요. ",
      content2: "색상코드는 Hex코드로 형식으로 입력가능합니다.(ex: #FFFFFF)",
      buttons: "ONLYOK",
      open: false,
      callback: () => {},
    });

    this.contourColorAlert = new DitapAlert({
      type: "warning",
      title: "경고",
      content1: "올바른 윤곽 색상값을 입력해주세요. ",
      content2: "색상코드는 Hex코드로 형식으로 입력가능합니다.(ex: #FFFFFF)",
      buttons: "ONLYOK",
      open: false,
      callback: () => {},
    });

    this.widthAlert = new DitapAlert({
      type: "warning",
      title: "경고",
      content1: "올바른 굵기값을 입력해주세요.",
      content2: "지름은 1이상의 숫자로 입력가능합니다.",
      buttons: "ONLYOK",
      open: false,
      callback: () => {},
    });
  }

  createResizeEvent() {
    super.createResizeEvent();
    // 컬러픽커 리사이즈 이벤트
    window.addEventListener(
      "resize",
      (this.resizeEvent = () => {
        const currentIsMobile = Utils.isBodyWidthLessThan600();

        if (this.isMobile !== currentIsMobile) {
          this.changeCloseBtn();
          this.isMobile = currentIsMobile;
        }

        const width = Utils.getColorPickerWidth();
        this.contourColorPicker.resize(width);
        this.colorPicker.resize(width);

        this.setMobilePopup();
      })
    );
  }

  setMobilePopup() {
    // 컬러픽커 모바일 팝업 이벤트
    const isMobile = Utils.isBodyWidthLessThan600();
    if (isMobile && this.colorBox.contains(this.colorPickBox)) {
      this.originInputValue = this.colorInput.value;
    }

    if (isMobile && this.contourColorBox.contains(this.contourColorPickBox)) {
      this.originContourInputValue = this.contourColorInput.value;
    }

    this.moveMobileContentPopup(
      this.colorBox,
      this.mobileColorPickerPopup,
      this.colorPickBox
    );
    this.moveMobileContentPopup(
      this.contourColorBox,
      this.mobileContourColorPickerPopup,
      this.contourColorPickBox
    );

    this.moveMobileContentPopup(
      this.typeSelectDiv,
      this.mobileTypeSelectPopup,
      this.typeOptionDiv
    );
  }

  moveMobileContentPopup(container, popup, content) {
    const isMobile = Utils.isBodyWidthLessThan600();
    if (isMobile && container.contains(content)) {
      container.removeChild(content);
      popup.appendContent(content);
    } else if (!isMobile && popup.content.contains(content)) {
      popup.content.removeChild(content);
      container.appendChild(content);
    }
  }

  removeResizeEvent() {
    window.removeEventListener("resize", this.resizeEvent);
  }

  /*
   * 선택 색상의 컬러 픽커.</br>
   * The color picker to select color.
   * @type {object}
   * @see {@link https://iro.js.org/colorPicker_api.html}
   */
  set colorPicker(colorPicker) {
    this._colorPicker = colorPicker;
  }
  get colorPicker() {
    return this._colorPicker;
  }

  /*
   * 외곽선 선택 색상의 컬러 픽커.</br>
   * The color picker to select color.
   * @type {object}
   * @see {@link https://iro.js.org/colorPicker_api.html}
   */
  set contourColorPicker(contourColorPicker) {
    this._contourColorPicker = contourColorPicker;
  }
  get contourColorPicker() {
    return this._contourColorPicker;
  }
  /**
   * 스타일 수정 할 라인스트링 엔티티.</br>
   * Linestring entity to modify the style.
   * @type {LinestringEntity}
   * @readonly
   */
  set linestringEntity(linestringEntity) {
    this._linestringEntity = linestringEntity;
  }
  get linestringEntity() {
    return this._linestringEntity;
  }
  /*
   * 버텍스 사용 설정 변경.</br>
   * Set vertex checkbox
   * @param {boolean} value 버택스 사용 여부.</br>Vertex Usage.
   */
  setCheckBox(state) {
    this.vertexCheckBox.checked = state;
  }

  /**
   * 위젯 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the widget.  Should be called if permanently
    removing the widget from layout.
   */
  destroy() {
    if (this.fullBtn instanceof Element) {
      this.fullBtn.removeEventListener("click", this.toggleExpandPopup);
    }
    super.destroy();
    this.removeResizeEvent();
    this.colorAlert.destroy();
    this.contourColorAlert.destroy();
    this.widthAlert.destroy();
    this.mobileColorPickerPopup.destroy();
    this.mobileContourColorPickerPopup.destroy();
    // this.observer.disconnect();
  }

  /*
   * 버튼 이벤트 생성.</br>
   * Create a button event.
   */
  createStyleBoxEvent() {
    // 취소 버튼
    this.cancleBtn.addEventListener("click", () => {
      this.close();
    });

    this.typeSelectInput.addEventListener("click", () => {
      this.typeSelectBtn.click();
    });

    // 스타일 - 셀렉트 박스 open/close
    this.typeSelectBtn.addEventListener("click", () => {
      if (this.typeOptionDiv.classList.contains("hide")) {
        this.typeOptionDiv.classList.remove("hide");
        this.typeSelectDiv.classList.add("on");
        this.mobileTypeSelectPopup.open();
      } else {
        this.typeOptionDiv.classList.add("hide");
        this.typeSelectDiv.classList.remove("on");
        this.mobileTypeSelectPopup.close();
      }
      this.setMobilePopup();
    });

    // 색상 - 색상 버튼 클릭
    this.colorPickerOpenBtn.addEventListener("click", () => {
      // 색상 박스 생성
      if (!this.mobileColorPickerPopup.isOpen) {
        this.colorPickBox.classList.remove("hide");
        this.mobileColorPickerPopup.open();
        this.originInputValue = this.colorInput.value;
        this.setMobilePopup();
      } else {
        this.colorPickBox.classList.add("hide");
        this.mobileColorPickerPopup.close();
        this.setMobilePopup();
      }
    });

    // 색상 - 색상 버튼 클릭
    this.contourColorPickerOpenBtn.addEventListener("click", () => {
      // 색상 박스 생성
      if (!this.mobileContourColorPickerPopup.isOpen) {
        this.contourColorPickBox.classList.remove("hide");
        this.mobileContourColorPickerPopup.open();
        this.originContourInputValue = this.contourColorInput.value;
        this.setMobilePopup();
      } else {
        this.contourColorPickBox.classList.add("hide");
        this.mobileContourColorPickerPopup.close();
        this.setMobilePopup();
      }
    });

    // 스타일 - spline 옵션 클릭
    Array.from(this.typeOptionDiv.getElementsByTagName("button")).forEach(
      (li) => {
        li.addEventListener("click", () => {
          this.typeOptionDiv.classList.add("hide");
          this.typeSelectDiv.classList.remove("on");
          this.typeSelectInput.textContent = li.textContent;
          this.mobileTypeSelectPopup.close();
          const type = li.nextElementSibling.getAttribute("value");
          this.typeSelectInput.nextElementSibling.classList = `ditap-style-line ditap-style-${type}-img`;
          this.selectLineType.setAttribute("value", type);

          this.contourColorBox.classList.add("hide");
          if (type === "outline") {
            this.contourColorBox.classList.remove("hide");
          } else {
            this.contourColorBox.classList.add("hide");
          }

          for (const allLi of this.typeOptionDiv.getElementsByTagName(
            "button"
          )) {
            allLi.querySelector(".ditap-radio-btn").classList.remove("on");
          }

          li.querySelector(".ditap-radio-btn").classList.add("on");
          this.setMobilePopup();
          this.mobileTypeSelectPopup.close();
        });
      }
    );

    // 굵기 - 굵기 크기 증가 버튼 클릭
    this.addWidthBtn.addEventListener("click", () => {
      const width = Number(this.widthInput.value);
      this.widthInput.value = width + 1;
    });

    // 굵기 - 굵기 크기 감소 버튼 클릭
    this.subtractWidthBtn.addEventListener("click", () => {
      const width = Number(this.widthInput.value);
      if (width - 1 > 0) {
        this.widthInput.value = width - 1;
      }
    });

    // 굵기 - 인풋 박스 변경시, 굵기값 변경
    this.widthInput.addEventListener("keyup", (e) => {
      const width = e.target.value;
      this.widthInput.value = width;
    });

    // 정점
    this.vertexBtn.addEventListener("click", () => {
      const check = !this.vertexCheckBox.checked;
      this.setCheckBox(check);
    });

    // 색상 - 선색상 이벤트 생성
    this.createColorPickerEvent(
      this.colorPicker,
      this.colorInput,
      this.colorLists,
      this.colorAlert,
      this.mobileColorInput
    );

    // 색상 - 면색상 이벤트 생성
    this.createColorPickerEvent(
      this.contourColorPicker,
      this.contourColorInput,
      this.contourColorLists,
      this.contourColorAlert,
      this.mobileContourColorInput
    );

    // 확인 - 엔티티 적용
    this.okBtn.addEventListener("click", () => {
      // 굵기
      const width = this.widthInput.value;
      let lineWidth = width;

      // 색상
      const color = this.colorInput.value;

      // 윤곽 색상
      const contourColor = this.contourColorInput.value;

      // 스타일
      const type = this.selectLineType.getAttribute("value");

      // 정점
      const vertex = !this.vertexCheckBox.checked;

      let useContourLine = false;
      if (type === "outline") {
        useContourLine = true;
      }

      if (Number(width) > 0 && width) {
        if (!this.checkAvailableColor(color)) {
          this.colorAlert.open();
        } else if (useContourLine && !this.checkAvailableColor(contourColor)) {
          this.contourColorAlert.open();
        } else {
          const ditapColor = Color.fromCssColorString(color);
          ditapColor.alpha = this.colorPicker.color.alpha;

          const depthFailColor = ditapColor.withAlpha(
            this.linestringEntity.depthFailAlpha
          );

          let material;
          let depthFailMaterial;
          if (type === "line") {
            material = ditapColor;
            depthFailMaterial = depthFailColor;
          } else if (type === "dotline") {
            material = new PolylineDashMaterialProperty({
              color: ditapColor,
            });

            depthFailMaterial = new PolylineDashMaterialProperty({
              color: depthFailColor,
            });
          } else if (type === "lightline") {
            material = new PolylineGlowMaterialProperty({
              color: ditapColor,
              glowPower: 1,
              taperPower: 0.5,
            });

            depthFailMaterial = new PolylineGlowMaterialProperty({
              color: depthFailColor,
              glowPower: 1,
              taperPower: 1,
            });

            lineWidth = width * 2;
          } else if (type === "outline") {
            const contourDitapColor = Color.fromCssColorString(contourColor);
            contourDitapColor.alpha = this.contourColorPicker.color.alpha;

            material = new PolylineOutlineMaterialProperty({
              color: ditapColor,
              outlineWidth: 3,
              outlineColor: contourDitapColor,
            });

            depthFailMaterial = new PolylineOutlineMaterialProperty({
              color: depthFailColor,
              outlineWidth: 3,
              outlineColor: contourDitapColor.withAlpha(
                this.linestringEntity.depthFailAlpha
              ),
            });
          } else if (type === "arrow") {
            material = new PolylineArrowMaterialProperty(ditapColor);
            depthFailMaterial = new PolylineArrowMaterialProperty(
              depthFailColor
            );

            lineWidth = width * 3;
          }

          this.linestringEntity.setAllEntitiesShow(true);
          this.linestringEntity.setPolylineWidth(lineWidth);
          this.linestringEntity.setPolylineMaterial(material);
          this.linestringEntity.setPolylineDepthFailMaterial(depthFailMaterial);
          this.linestringEntity.setAllPointsOutlineColor(ditapColor);

          if (vertex) {
            this.linestringEntity.setAllPointEntitiesShow(true);
            this.linestringEntity.setAllPointsPixelSize(Number(width) * 2 + 2);
          } else {
            this.linestringEntity.setAllPointEntitiesShow(false);
          }

          this.close();
        }
      } else {
        this.widthAlert.open();
      }
    });
  }

  /**
   * 팝업 닫기.</br>
   * Close PopupStyleLinestring.
   */
  close() {
    super.close();
    this.mobileColorPickerPopup.close();
    this.mobileColorPickerPopup.destroy();
    this.mobileContourColorPickerPopup.close();
    this.mobileContourColorPickerPopup.destroy();
    this.mobileTypeSelectPopup.close();
    this.mobileTypeSelectPopup.destroy();
  }

  /*
   * 색상 변경 팝업 요소 생성.</br>
   * Create PopupStyleLinestring Element
   * @returns {object} PopupStyleLinestring 요소.</br>The PopupStyleLinestring Element.
   */
  createStyleBox() {
    this.wrapper.classList.add(
      "ditap-mobile-bottom",
      "header-top",
      "draggable"
    );
    // # 헤더
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = "스타일";

    // # 컨텐츠
    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "아래의 스타일을 선택해주세요.";
    const styleList = document.createElement("div");
    styleList.classList.add("ditap-popup-style-list");

    styleList.innerHTML = `<div class="ditap-style-item">
    <p class="ditap-style-title js-ditap-style-width-box">굵기</p>
    <div class="ditap-style-inp-wrap">
      <label for="width1">
        <input id="width1" type="number" class="ditap-style-inp js-ditap-style-width" value="5" />
      </label>
      <button class="ditap-add-btn js-ditap-style-add-width ditap-custom-button"></button>
      <button class="ditap-subtract-btn js-ditap-style-subtract-width ditap-custom-button"></button>
    </div>
  </div>
  
  <div class="ditap-style-item ditap-style-clipping-item">
    <p class="ditap-style-title">정점</p>
    <p class="ditap-clipping-item js-ditap-vertex-item" style="">
      <input class="ditap-chk js-ditap-vertex-check-box" type="checkbox" value="X">
      <button class="ditap-label ditap-custom-button">
        <span class="ditap-label-title">도형의 정점을 강조하여 표시합니다.</span>
      </button>
	  </p>
  </div>
  
  <div class="ditap-style-item ditap-line">
    <p class="ditap-style-title">스타일</p>
    <div class="ditap-style-inp-wrap ditap-style-select js-ditap-style-select">
      <div class="ditap-style-inp js-ditap-style-type">실선</div>
      <p class="ditap-style-line ditap-style-line-img" style=""></p>
      <input class="js-ditap-line-type" type="hidden" value="line">
      <button class="ditap-dropbox-img ditap-custom-button js-ditap-style-select-btn"></button>
      <div class="ditap-style-opt js-ditap-style-type-opt hide">
        <ul class="ditap-style-opt-list">
        
          <button class="ditap-style-opt-title ditap-custom-button">
            <div class="ditap-style-opt-line-title">실선</div>
            <div class="ditap-radio-btn on"></div>
            <p class="ditap-style-line ditap-style-line-img" style=""></p>
          </button>
          <input  type="hidden" value="line">
          <button class="ditap-style-opt-title ditap-custom-button ">
            <div class="ditap-style-opt-line-title">점선</div>
            <div class="ditap-radio-btn"></div>
            <p class="ditap-style-line ditap-style-dotline-img" style=""></p>
          </button>
          <input type="hidden" value="dotline">
          <button class="ditap-style-opt-title ditap-custom-button">
            <div class="ditap-style-opt-line-title">광선</div>
            <div class="ditap-radio-btn"></div>
            <p class="ditap-style-line ditap-style-lightline-img" style=""></p>
          </button>
          <input type="hidden" value="lightline">
          <button class="ditap-style-opt-title ditap-custom-button">
            <div class="ditap-style-opt-line-title">외곽선</div>
            <div class="ditap-radio-btn"></div>
            <p class="ditap-style-line ditap-style-outline-img" style=""></p>
          </button>
          <input type="hidden" value="outline">
          <button class="ditap-style-opt-title ditap-custom-button">
            <div class="ditap-style-opt-line-title">화살표</div>
            <div class="ditap-radio-btn"></div>
            <p class="ditap-style-line ditap-style-arrow-img" style=""></p>
          </button>
          <input type="hidden" value="arrow">
        </ul>
      </div>
    </div>
  </div>
  <div class="ditap-style-color-wrap js-ditap-style-color-box">
    <div class="ditap-style-item">
      <p class="ditap-style-title">색상</p>
      <div class="ditap-style-inp-wrap js-ditap-style-color-btn">
        <input type="text" class="ditap-style-inp js-ditap-style-color" maxlength="7" value="#FFFFFF"></input>
        <button class="ditap-palette-img js-ditap-color-open-btn ditap-custom-button"></button>
      </div>
    </div>
    <div class="ditap-style-color  js-ditap-style-color-pickBox hide">
      <div class="js-ditap-style-color-picker"> </div>
      <div style="height: 100px;">
          <ul class="ditap-style-color-list js-ditap-style-color-list">
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6127FF;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #0067F4;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #00D0FF;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1BC47D;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6FCF97;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF2C60;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF5A83;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #000000;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1C1C1C;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #333333;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #828282;"></button>
            <button class="ditap-style-color-item ditap-custom-button" style="background-color: #BDBDBD;"></button>
          </ul>
      </div>
    </div>
  </div>
  <div class="ditap-style-color-wrap js-ditap-style-contour-color-box hide">
    <div class="ditap-style-item">
      <p class="ditap-style-title">윤곽 색상</p>
      <div class="ditap-style-inp-wrap js-ditap-style-color-btn">
        <input type="text" class="ditap-style-inp js-ditap-style-color" maxlength="7" value="#FFFFFF"></input>
        <button class="ditap-palette-img js-ditap-color-open-btn ditap-custom-button"></button>
      </div>
    </div>
    <div class="ditap-style-color  js-ditap-style-color-pickBox hide">
      <div class="js-ditap-style-color-picker"> </div>
      <div style="height: 100px;">
        <ul class="ditap-style-color-list js-ditap-style-color-list">
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6127FF;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #0067F4;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #00D0FF;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1BC47D;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6FCF97;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF2C60;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF5A83;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #000000;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1C1C1C;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #333333;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #828282;"></button>
          <button class="ditap-style-color-item ditap-custom-button" style="background-color: #BDBDBD;"></button>
        </ul>
      </div>
    </div>
  </div>

  `;

    const btnWrap = document.createElement("div");
    btnWrap.classList.add("ditap-btn-wrap");
    btnWrap.innerHTML = `<button class="ditap-popup-btn js-ditap-style-cancle-btn">취소</button><button class="ditap-popup-btn js-ditap-style-ok-btn gold">확인</button>`;

    return {
      header: {
        title: title,
      },
      content: {
        subTitle: subTitle,
        styleList: styleList,
      },
      footer: {
        btnWrap: btnWrap,
      },
    };
  }

  /*
   * 컬러픽커 생성.</br>
   * Create ColorPicker to change the style of a LinestringEntity.
   * @param {Element} pickerDiv 부모 요소.</br>The Element of pickerDiv.
   * @returns {iro.ColorPicker} 생성된 컬러픽커.</br>The ColorPicker.
   */
  createColorPicker(pickerDiv) {
    // 색상 - 컬러픽커 생성
    const width = Utils.getColorPickerWidth();

    const colorPicker = new iro.ColorPicker(pickerDiv, {
      width: width,
      boxHeight: 180,
      color: "rgb(0, 0, 0)",
      // borderWidth: 1,
      // borderColor: "#fff",
      layout: [
        {
          component: iro.ui.Box,
        },
        {
          component: iro.ui.Slider,
          options: {
            sliderType: "hue",
            sliderSize: 10,
          },
        },
        {
          component: iro.ui.Slider,
          options: {
            sliderType: "alpha",
            sliderSize: 10,
          },
        },
      ],
    });

    return colorPicker;
  }

  changeColorPickerByInput(color, colorPicker, alert, colorInput) {
    if (color.length === 7) {
      if (this.checkAvailableColor(color)) {
        // 16진수 -> 10진수
        const byteColor = Color.fromCssColorString(color).toBytes();
        colorPicker.color.red = byteColor[0];
        colorPicker.color.green = byteColor[1];
        colorPicker.color.blue = byteColor[2];
      } else {
        alert.open();
        colorInput.value = "#FFFFFF";
        colorPicker.color.red = 255;
        colorPicker.color.green = 255;
        colorPicker.color.blue = 255;
      }
    }
  }

  /*
   * 컬러픽커 이벤트 생성.</br>
   * Create a color picker event.
   * @param {Element} colorBtn 닫기 버튼 요소.</br>The Element of colorBtn.
   * @param {Element} colorPickBox 컬러픽커 박스 요소.</br>The Element of colorPickBox.
   * @param {Element} colorPicker 컬러픽커 요소.</br>The Element of colorPicker.
   * @param {Element} colorInput 색상 입력 요소.</br>The Element of colorInput.
   * @param {Element} colorLists 색상 목록 요소.</br>The Element of colorLists.
   * @param {Element} otherColorPickBox 외곽선 컬러픽커 박스 요소.</br>The Element of otherColorPickBox.
   */
  createColorPickerEvent(
    colorPicker,
    colorInput,
    colorLists,
    alert,
    mobileColorInput
  ) {
    // 색상 - 컬러픽커에서 색상변경시, 색생값 변경
    colorPicker.on("color:change", (color) => {
      const hex = Color.fromBytes(
        color.red,
        color.green,
        color.blue
      ).toCssHexString();

      // 색상 인풋박스 값 변경
      colorInput.value = hex.toUpperCase();
      mobileColorInput.value = hex.toUpperCase();
    });

    // 색상 - 인풋 박스 변경시, 색상값 변경
    colorInput.addEventListener("keyup", (e) => {
      const color = e.target.value.toUpperCase();
      this.changeColorPickerByInput(color, colorPicker, alert, colorInput);
    });

    // 색상 - 인풋 박스 변경시, 색상값 변경
    mobileColorInput?.addEventListener("keyup", (e) => {
      const color = e.target.value.toUpperCase();
      this.changeColorPickerByInput(color, mobileColorInput);
    });

    // 색상 - 리스트 클릭 시, 색상값 변경
    Array.from(colorLists).forEach((colorLi) => {
      colorLi.addEventListener("click", () => {
        const rgbColor = window.getComputedStyle(colorLi).backgroundColor;

        const byteColor = Color.fromCssColorString(rgbColor).toBytes();
        colorPicker.color.red = byteColor[0];
        colorPicker.color.green = byteColor[1];
        colorPicker.color.blue = byteColor[2];
      });
    });
  }

  /*
   * 기존 엔티티 스타일 적용.</br>
   * Apply the style of the existing Entity.
   * @param {PolygonEntity} polygonEntity 스타일 적용할 LinestringEntity 인스턴스.</br> The instance of LinestringEntity to apply
   */
  setPopupStyleLinestring(linestringEntity) {
    // 팝업이 열릴 때, 기존 엔티티의 스타일을 팝업에 적용시킨다.
    const polyline = linestringEntity.polylineEntity.polyline;
    if (polyline) {
      const isShowPolyline = polyline.show.getValue();

      if (isShowPolyline) {
        // INPUT 색상 적용
        const color = polyline.material.color.getValue();
        this.colorPicker.color.red = color.red * 255;
        this.colorPicker.color.green = color.green * 255;
        this.colorPicker.color.blue = color.blue * 255;
        this.colorPicker.color.alpha = color.alpha;

        // INPUT 스타일 적용
        let style = "";
        let material = "line";
        let width = polyline.width.getValue();
        if (polyline.material instanceof ColorMaterialProperty) {
          style = "실선";
          material = "line";
        } else if (polyline.material instanceof PolylineDashMaterialProperty) {
          style = "점선";
          material = "dotline";
        } else if (polyline.material instanceof PolylineGlowMaterialProperty) {
          style = "광선";
          material = "lightline";
          width = width / 2;
        } else if (
          polyline.material instanceof PolylineOutlineMaterialProperty
        ) {
          style = "외곽선";
          material = "outline";

          this.contourColorBox.classList.remove("hide");
          const color = polyline.material.outlineColor.getValue();
          if (color) {
            this.contourColorPicker.color.red = color.red * 255;
            this.contourColorPicker.color.green = color.green * 255;
            this.contourColorPicker.color.blue = color.blue * 255;
            this.contourColorPicker.color.alpha = color.alpha;
          }
        } else if (polyline.material instanceof PolylineArrowMaterialProperty) {
          style = "화살표";
          material = "arrow";
          width = width / 3;
        }

        this.typeSelectInput.textContent = style;
        this.typeSelectInput.nextElementSibling.classList = `ditap-style-line ditap-style-${material}-img`;
        this.selectLineType.setAttribute("value", material);

        // INPUT 굵기 적용
        this.widthInput.value = width;
      }
    }

    // 정점 적용
    const pointEntities = linestringEntity.pointEntities;

    // 정점 체크박스 체크
    if (pointEntities.length > 0 && pointEntities[0].point.show.getValue()) {
      this.setCheckBox(false);
    } else {
      this.setCheckBox(true);
    }
  }

  /*
   * 아이콘 유형 모드의 팝업 열기.</br>
   * Opens a PopupStylePoint of icon type mode.
   */
  openIconType() {
    // 굵기, 색상 Box 활성화, icon Box 비활성화
    this.widthBox.classList.add("hide");
    this.colorBox.classList.add("hide");
    this.contourColorBox.classList.remove("hide");

    this.typeOptionDiv.classList.add("hide");
    this.typeSelectDiv.classList.remove("on");
    this.typeSelectInput.textContent = this.iconType.textContent;
  }

  /*
   * HEX 색상 코드를 사용 여부 조회.</br>
   * Check if it is a usable HEX color code.
   * @param {string} color 확인할 색상 코드.</br>Color code to check(ex: "#000000").
   * @returns {boolean} 사용 가능 여부.</br>Usable Status.
   */
  checkAvailableColor(color) {
    if (Color.fromCssColorString(color)) {
      return true;
    }

    return false;
  }
}
export default PopupStyleLinestring;
